import { MarkdownContent } from '@/components/shared/MarkdownContent';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import {
  formatMessageTime,
  getRoleInitials,
  type ChatMessage as ChatMessageType,
} from '@/types/chat';
import { Bot, User } from 'lucide-react';

interface ChatMessageProps {
  message: ChatMessageType;
  isOptimistic?: boolean;
}

export function ChatMessage({ message, isOptimistic = false }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  return (
    <div
      className={cn('flex gap-3 p-4', isUser && 'flex-row-reverse', isOptimistic && 'opacity-70')}
    >
      <Avatar className={cn('h-8 w-8 shrink-0', isUser ? 'bg-primary' : 'bg-muted')}>
        <AvatarFallback className={cn(isUser ? 'bg-primary text-primary-foreground' : 'bg-muted')}>
          {isUser ? (
            <User className="h-4 w-4" />
          ) : isAssistant ? (
            <Bot className="h-4 w-4" />
          ) : (
            getRoleInitials(message.role)
          )}
        </AvatarFallback>
      </Avatar>

      <div className={cn('flex flex-col gap-1 max-w-[80%]', isUser && 'items-end')}>
        <div
          className={cn(
            'rounded-lg px-4 py-2 text-sm',
            isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
          )}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
          ) : (
            <MarkdownContent content={message.content} />
          )}
        </div>

        <span className="text-xs text-muted-foreground">
          {isOptimistic ? 'Sending...' : formatMessageTime(message.created_at)}
        </span>
      </div>
    </div>
  );
}
